/* eslint-disable no-await-in-loop */
import { expect } from '@open-wc/testing';

import {
  executeServerCommand,
  sendKeys,
  sendMouse,
} from '@web/test-runner-commands';
import {
  fixtureBeforeEach,
  fixtureBefore,
  fixtureAfterEach,
  TelemInfo,
  screenshotAndCompare,
  //  mockDataMeta
} from './utils/helpers.js';
import { NotificationsTestHarness } from '../src/testHarness.js';

describe('date filter', () => {
  let dateFilterDropDownInfo: TelemInfo[] = [];
  let testHarness: NotificationsTestHarness;

  before(async () => {
    await fixtureBefore();
  });

  beforeEach(async () => {
    testHarness = await fixtureBeforeEach();

    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-cta-filter-date"] md-icon-button',
    });

    await sendKeys({
      press: 'Space',
    });

    dateFilterDropDownInfo = await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-date-filter-dropdown"]',
      action: 'info',
    });
  });

  afterEach(async () => {
    fixtureAfterEach();
  });

  it('should be open and toggle visibility when clicking the button', async () => {
    expect(dateFilterDropDownInfo[0].isVisible).to.be.true;

    await screenshotAndCompare('filter-date-dropdown-open');

    await sendKeys({
      press: 'Space',
    });

    dateFilterDropDownInfo = await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-date-filter-dropdown"]',
      action: 'info',
    });

    expect(dateFilterDropDownInfo[0].isVisible).to.be.false;

    await screenshotAndCompare('filter-date-dropdown-closed');
  });

  it('should close when clicking outside the dropdown', async () => {
    const headingPosition: [number, number] = await executeServerCommand(
      'mouseFocusInfo',
      {
        selector: '[data-testid="notifications-heading"]',
        action: 'mouse',
      }
    );

    await sendMouse({
      type: 'click',
      position: headingPosition,
    });

    dateFilterDropDownInfo = await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-date-filter-dropdown"]',
      action: 'info',
    });

    expect(dateFilterDropDownInfo[0].isVisible).to.be.false;
  });

  it('should close when clicking cancel', async () => {
    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-cancel-date-filter"]',
    });

    await sendKeys({
      press: 'Space',
    });

    dateFilterDropDownInfo = await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-date-filter-dropdown"]',
      action: 'info',
    });

    expect(dateFilterDropDownInfo[0].isVisible).to.be.false;
  });

  it('should trigger validation on the from date field', async () => {
    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-from-date"]',
    });

    await sendKeys({
      press: 'Backspace',
    });

    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-activate-date-filter"]',
    });

    await sendKeys({
      press: 'Space',
    });

    const fromValidationError: TelemInfo[] = await executeServerCommand(
      'mouseFocusInfo',
      {
        selector: '[data-testid="notifications-from-date"] ix-field.error',
        action: 'info',
      }
    );

    expect(fromValidationError.length).to.equal(1);

    await screenshotAndCompare('filter-date-dropdown-from-error');
  });

  it('should trigger validation on the to date field', async () => {
    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-to-date"]',
    });

    await sendKeys({
      press: 'Backspace',
    });

    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-activate-date-filter"]',
    });

    await sendKeys({
      press: 'Space',
    });

    const fromValidationError: TelemInfo[] = await executeServerCommand(
      'mouseFocusInfo',
      {
        selector: '[data-testid="notifications-to-date"] ix-field.error',
        action: 'info',
      }
    );

    expect(fromValidationError.length).to.equal(1);

    await screenshotAndCompare('filter-date-dropdown-to-error');
  });

  it('should filter out items which do not match the date range and clearing the filter should restore them', async () => {
    const formatDate = (date: Date) => ({
      day: date.getDate().toString().padStart(2, '0'),
      month: (date.getMonth() + 1).toString().padStart(2, '0'),
      year: date.getFullYear().toString(),
    });

    // Get today's date
    const today = new Date();

    // Get yesterday's date
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    // Format all dates
    const now = formatDate(today);
    const prev = formatDate(yesterday);

    const ixDates: any = testHarness
      .shadowRoot!.querySelector('ix-notifications')!
      .shadowRoot!.querySelector('ix-drawer div[slot="content"] date-filters')!
      .shadowRoot!.querySelectorAll('ix-date');

    ixDates[0].shadowRoot!.querySelector(
      'ix-field input'
    ).value = `${prev.year}-${prev.month}-${prev.day}`;
    ixDates[0].onChanged(`${prev.year}-${prev.month}-${prev.day}`);
    ixDates[1].shadowRoot!.querySelector(
      'ix-field input'
    ).value = `${now.year}-${now.month}-${now.day}`;
    ixDates[1].onChanged(`${now.year}-${now.month}-${now.day}`);

    await screenshotAndCompare('filter-data-dropdown-populated');

    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-activate-date-filter"]',
    });

    await sendKeys({
      press: 'Space',
    });

    let notificationItemInfo: TelemInfo[] = await executeServerCommand(
      'mouseFocusInfo',
      {
        selector: 'notification-item',
        action: 'info',
      }
    );

    expect(notificationItemInfo.length).to.equal(1);

    await screenshotAndCompare('filter-date-applied');

    await executeServerCommand('mouseFocusInfo', {
      selector: '[data-testid="notifications-cta-filter-date"] md-icon-button',
    });

    await sendKeys({
      press: 'Space',
    });

    await executeServerCommand('mouseFocusInfo', {
      selector:
        '[data-testid="notifications-clear-date-filter"] md-icon-button',
    });

    await sendKeys({
      press: 'Space',
    });

    notificationItemInfo = await executeServerCommand('mouseFocusInfo', {
      selector: 'notification-item',
      action: 'info',
    });

    // TODO: Fix this functionality
    // expect(notificationItemInfo.length).to.equal(mockDataMeta.itemsCount);

    await screenshotAndCompare('filter-date-cleared');
  });
});
